import { ToastBody, ToastBold } from '@/components/shared/Toast'
import { Trans, useTranslation } from 'next-i18next'

import Link from 'next/link'
import React from 'react'
import clsx from 'clsx'
import { sendAnalytics } from '@audio-network/analytics'
import sidebarStyles from '../Sidebar.module.css'
import styles from './ProjectList.module.css'
import { toast } from '@/utils/notifications/toast'
import useProjects from '@/stores/projectStore'

const ProjectList = (): JSX.Element => {
	const { projects, addTrackToProject } = useProjects()
	const { t } = useTranslation(['addtoproject'])

	if (!projects || projects.length === 0) {
		return
	}

	const handleClick = (): void => {
		sendAnalytics({
			noun: 'Project',
			pastTenseVerb: 'Clicked',
			eventParameters: {
				Location: 'Music App Navigation',
			},
		})
	}

	const handleDragOver = (event: React.DragEvent): void => {
		event.preventDefault()

		const element = event.currentTarget as HTMLElement
		element.classList.add(styles.projectDragOver)
	}

	const handleDragLeave = (event: React.DragEvent): void => {
		event.preventDefault()

		const element = event.currentTarget as HTMLElement
		element.classList.remove(styles.projectDragOver)
	}

	const handleDrop = async (
		event: React.DragEvent,
		projectId: string
	): Promise<void> => {
		event.preventDefault()
		event.stopPropagation()

		const element = event.currentTarget as HTMLElement
		element.classList.remove(styles.projectDragOver)

		const data = event.dataTransfer.getData('text/plain')
		if (!data) return

		let track: DragTrackData
		try {
			track = JSON.parse(data) as DragTrackData
			if (!track?.id || !track?.title) return
		} catch {
			// Silently ignore invalid data to stop console errors
			return
		}

		try {
			await addTrackToProject(projectId, track.id)

			sendAnalytics({
				noun: 'Add to Project',
				pastTenseVerb: 'Completed',
				eventParameters: {
					TrackId: track.id,
					TrackTitle: track.title,
					AlbumNo: track.albumNumber,
					TrackSource: track.trackSource,
					Catalogue: track.catalogue,
					Genre: track.genre,
					Location: 'Drag and Drop',
				},
			})

			toast.success(
				<ToastBody>
					<Trans components={{ bold: <ToastBold /> }}>
						{t('toast-success-message', {
							trackInfo: track.title,
							project: projects.find((project) => project.Id === projectId)
								?.Name,
						})}
					</Trans>
				</ToastBody>
			)
		} catch (error) {
			toast.error(t('toast-error-message'))
		}
	}

	return (
		<ul className={clsx(styles.projectList, sidebarStyles.showWhenExpanded)}>
			{projects.map((project) => (
				<li key={project.Id} className={styles.projectListItem}>
					<Link
						className={styles.project}
						href={`/project/edit/${project.Id}`}
						onClick={handleClick}
						onDragOver={handleDragOver}
						onDragLeave={handleDragLeave}
						onDrop={(event) => handleDrop(event, project.Id)}
					>
						{project.Name}
					</Link>
				</li>
			))}
		</ul>
	)
}

export default ProjectList
